import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'

import { SearchInput } from 'app/common/presentation'
import { ClockIcon, DatabaseIcon, PlusIcon } from 'app/common/presentation/icons'
import { getInitials } from 'app/common/presentation/initials'
import { useConnections } from 'app/connections/application'
import { useEvents } from 'app/events/application'
import { useObjectives } from 'app/objectives/application'

import { Avatar, Button, Chip, PageBanner, Table } from 'components'

import styles from './Objectives.module.scss'

const Objectives = () => {
  const navigate = useNavigate()
  const { t } = useTranslation('objectives')
  const { objectives } = useObjectives()
  const { connections } = useConnections()
  const { getEvents } = useEvents()
  const hasConnections = connections.length > 0

  const [search, setSearch] = useState('')
  const [eventsLoading, setEventsLoading] = useState(false)
  const [numberOfEvents, setNumberOfEvents] = useState<number>()

  const fetchEvents = useCallback(async () => {
    setEventsLoading(true)
    setNumberOfEvents(await getEvents().then((events) => events.length))
    setEventsLoading(false)
  }, [getEvents])

  useEffect(() => {
    if (hasConnections) {
      void fetchEvents()
    }
  }, [fetchEvents, hasConnections])

  const isWaitingForEvents = hasConnections && numberOfEvents === 0

  const filteredObjectives = useMemo(() => {
    const lowerCasedSearch = search.toLowerCase()

    return objectives.filter((objective) => {
      if (lowerCasedSearch) {
        return (
          objective.name.toLocaleLowerCase().includes(lowerCasedSearch) ||
          objective.status.toLocaleLowerCase().includes(lowerCasedSearch)
        )
      }

      return true
    })
  }, [objectives, search])

  return (
    <div className={styles.objectives}>
      <PageBanner subtitle={t('description')} title={t('title')}>
        <Button
          disabled={!hasConnections || numberOfEvents === undefined || numberOfEvents === 0}
          onClick={() => void navigate('/objectives/create')}
          rightIcon={<PlusIcon />}
        >
          {t('actions.newObjective')}
        </Button>
      </PageBanner>

      {!hasConnections ? (
        <div className={styles.emptyStateContainer}>
          <div className={styles.emptyState}>
            <div className={styles.emptyStateContent}>
              <div className={styles.emptyStateIcon}>
                <DatabaseIcon />
              </div>
              <div className={styles.emptyStateText}>
                <h2>{t('overview.noConnection.title')}</h2>
                <p>{t('overview.noConnection.description')}</p>
              </div>
            </div>
            <Button onClick={() => void navigate('/connections/browse')}>
              {t('overview.noConnection.action')}
            </Button>
          </div>
        </div>
      ) : isWaitingForEvents ? (
        <div className={styles.emptyStateContainer}>
          <div className={`${styles.emptyState} ${styles.waitingState}`}>
            <div className={styles.emptyStateContent}>
              <ClockIcon className={styles.waitingStateIcon} />
              <div className={styles.emptyStateText}>
                <h2>{t('overview.noEvents.title')}</h2>
                <p>{t('overview.noEvents.description')}</p>
              </div>
              <div className={styles.connectionStatus}>
                <span />
                {t('overview.noEvents.status')}
              </div>
            </div>
            <Button disabled={eventsLoading} onClick={() => void fetchEvents()} variant="outlined">
              {t('overview.noEvents.action')}
            </Button>
          </div>
        </div>
      ) : numberOfEvents !== undefined && numberOfEvents !== 0 ? (
        <>
          <div className={styles.searchBar}>
            <SearchInput
              className={styles.search}
              onChange={setSearch}
              placeholder={t('overview.search')}
              value={search}
            />
          </div>

          <div className={styles.tableContainer}>
            <Table.Table
              className={styles.table}
              columns={[
                t('overview.table.name'),
                t('overview.table.status'),
                t('overview.table.models'),
                t('overview.table.signals'),
                t('overview.table.createdBy'),
              ]}
              data={filteredObjectives}
              renderRow={(objective) => (
                <Table.Row key={objective.id}>
                  <Table.Cell className={styles.nameCell}>
                    <Link to={`/objectives/${objective.id}`}>{objective.name}</Link>
                  </Table.Cell>
                  <Table.Cell>
                    <Chip color={objective.status === 'active' ? 'green' : 'neutral'}>
                      {t(
                        `overview.status.${objective.status === 'active' ? 'active' : 'inactive'}`,
                      )}
                    </Chip>
                  </Table.Cell>
                  <Table.Cell>{t('overview.emptyValue')}</Table.Cell>
                  <Table.Cell>{t('overview.emptyValue')}</Table.Cell>
                  <Table.Cell className={styles.creatorCell}>
                    {objective.createdBy ? (
                      <>
                        <Avatar
                          className={styles.creatorAvatar}
                          initials={getInitials(objective.createdBy)}
                        />
                        <span>
                          {objective.createdBy.firstName} {objective.createdBy.lastName}
                        </span>
                      </>
                    ) : (
                      t('overview.emptyValue')
                    )}
                  </Table.Cell>
                </Table.Row>
              )}
            />
          </div>
        </>
      ) : null}
    </div>
  )
}

export default Objectives
